import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiMail, FiLock, FiArrowRight } from 'react-icons/fi';
import { usePlatformAuth } from '../../../utils/PlatformAuthContext';
import { loginlogo } from '../../../components/imagepath';
import ClientsMap from '../../../components/Platform/ClientsMap';

const PlatformLogin = () => {
  const { login } = usePlatformAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Completa todos los campos');
      return;
    }

    setLoading(true);
    const result = await login(email.trim(), password);
    setLoading(false);

    if (result.success) {
      navigate('/tenants');
    } else {
      setError(result.message);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Panel izquierdo — mapa de clientes */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative overflow-hidden bg-gradient-to-br from-rentahab-700 via-rentahab-600 to-rentahab-900">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl animate-float" />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)',
              backgroundSize: '32px 32px',
            }}
          />
        </div>

        <div className="relative z-10 flex flex-col p-10 xl:p-14 w-full min-h-screen">
          <div className="animate-fade-in mb-8">
            <img src={loginlogo} alt="RentAHAB" className="h-10 brightness-0 invert" />
          </div>

          <div className="animate-fade-up mb-6">
            <p className="text-rentahab-200 text-xs font-semibold uppercase tracking-widest mb-2">
              Red de edificios
            </p>
            <h1 className="text-2xl xl:text-3xl font-extrabold text-white leading-snug">
              Propiedades gestionadas con RentAHAB
            </h1>
            <p className="mt-2 text-sm text-rentahab-100/70 max-w-sm">
              Cada pin es un edificio de nuestros clientes, en distintas ciudades y provincias.
            </p>
          </div>

          <div className="flex-1 flex flex-col min-h-0 animate-fade-up">
            <ClientsMap />
          </div>

          <p className="text-rentahab-300/50 text-xs mt-6 animate-fade-in">
            © {new Date().getFullYear()} RentAHAB · Mapa © OpenStreetMap contributors
          </p>
        </div>
      </div>

      {/* Panel derecho — formulario */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden mb-10 text-center">
            <img src={loginlogo} alt="RentAHAB" className="h-9 mx-auto" />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Bienvenido
            </h2>
            <p className="mt-2 text-slate-500 text-sm sm:text-base">
              Ingresa tus credenciales de administrador para acceder a la plataforma.
            </p>
          </div>

          {error && (
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3.5 text-sm text-red-700">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Correo electrónico
              </label>
              <div
                className={`relative flex items-center rounded-xl border bg-white transition-all duration-200 ${
                  focusedField === 'email'
                    ? 'border-rentahab-500 ring-4 ring-rentahab-500/10 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <FiMail className="absolute left-4 w-[18px] h-[18px] text-slate-400 pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="superadmin@rentahab.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full py-3.5 pl-11 pr-4 bg-transparent text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none rounded-xl"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                Contraseña
              </label>
              <div
                className={`relative flex items-center rounded-xl border bg-white transition-all duration-200 ${
                  focusedField === 'password'
                    ? 'border-rentahab-500 ring-4 ring-rentahab-500/10 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <FiLock className="absolute left-4 w-[18px] h-[18px] text-slate-400 pointer-events-none" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full py-3.5 pl-11 pr-12 bg-transparent text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-50"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <FiEyeOff className="w-[18px] h-[18px]" /> : <FiEye className="w-[18px] h-[18px]" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-rentahab-500 hover:bg-rentahab-600 active:bg-rentahab-700 text-white font-semibold text-sm transition-all duration-200 shadow-lg shadow-rentahab-500/25 hover:shadow-rentahab-500/40 disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none mt-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Ingresando...
                </>
              ) : (
                <>
                  Ingresar a RentAHAB
                  <FiArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-slate-400">
            Acceso restringido a administradores autorizados de la plataforma.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlatformLogin;
